import {
    BrowserModule,
    HAMMER_GESTURE_CONFIG,
} from "@angular/platform-browser";
import { APP_INITIALIZER, Injectable, NgModule } from "@angular/core";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AccountModule } from "./account/account.module";
import { JwtModule } from "@auth0/angular-jwt";
import { HttpClientModule } from "@angular/common/http";
import { AuthModule } from "./auth/auth.module";
import { CoreModule } from "./core/core.module";
import { environment } from "../environments/environment";
import { DatasetModule } from "./dataset/dataset.module";
import { MaiaModule } from "./maia/maia.module";
import { FieldsModule } from "./fields/fields.module";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { AgmCoreModule, LAZY_MAPS_API_CONFIG, LazyMapsAPILoaderConfigLiteral } from "@agm/core";
import { GroupPermissionLoginComponent } from "./auth/group-permission-login/group-permission-login.component";
import { RouterModule } from "@angular/router";
import { SharedModule } from "./shared/shared.module";
import { GeographicalRegionsComponent } from "./geographical-regions/geographical-regions.component";
import { DialogUpdateRegionComponent } from "./geographical-regions/dialog-update-region/dialog-update-region.component";
import { ExternalOverlaysComponent } from "./external-overlays/external-overlays.component";
import { EditExternalOverlayTreeComponent } from "./external-overlays/edit-external-overlay-tree/edit-external-overlay-tree.component";
import { LicenseManager } from "ag-grid-enterprise";
import { OverlayComponent } from "./overlay/overlay.component";
import { OverlayModule } from "./overlay/overlay.module";
import { NgxEchartsModule } from "ngx-echarts";
import { CommonModule } from "@angular/common";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { GoogleAPIKeyService } from "./core/services/google-api-key.service";
import { GoogleMapsInitializer } from "./core/services/google-maps-initializer.service";
import { RecaptchaV3Module, ReCaptchaV3Service, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { CustomScoringComponent } from './account/custom-scoring/custom-scoring.component';
import { AddCustomScoringComponent } from "./account/custom-scoring/add-custom-scoring/add-custom-scoring.component";
import { ViewGlobalDataSetComponent } from "./account/custom-scoring/view-global-dataset/view-global-dataset.component";
import { LayerExposedComponent } from "./account/layer-exposed/layer-exposed.component";
import { AddLayerExposedComponent } from "./account/layer-exposed/add-layer-exposed/add-layer-exposed.component";
import { ConsolidatedRiskScoreComponent } from "./account/consolidated-risk-score/consolidated-risk-score.component";
import { ConsolidatedRiskScoreListComponent } from "./account/consolidated-risk-score/consolidated-risk-score-list/consolidated-risk-score-list.component";
import { EditProfileDialogComponent } from "./account/consolidated-risk-score/edit-profile-dialog/edit-profile-dialog.component";
import { AnalyticsInitializerService } from './core/services/analytics-initializer.service';

LicenseManager.setLicenseKey(environment.agGridLicense);

export function echartImport(): any {
    return import("echarts");
}

export function tokenGetter(): string {
    const token = localStorage.getItem("access_token");
    return token || "invalid";
}

@NgModule({
    declarations: [
        AppComponent,
        GroupPermissionLoginComponent,
        GeographicalRegionsComponent,
        DialogUpdateRegionComponent,
        ExternalOverlaysComponent,
        EditExternalOverlayTreeComponent,
        CustomScoringComponent,
        AddCustomScoringComponent,
        ViewGlobalDataSetComponent,
        LayerExposedComponent,
        AddLayerExposedComponent,
        ConsolidatedRiskScoreComponent,
        ConsolidatedRiskScoreListComponent,
        EditProfileDialogComponent,
    ],
    imports: [
        CoreModule,
        SharedModule,
        AccountModule,
        AuthModule,
        FieldsModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        HttpClientModule,
        RouterModule,
        OverlayModule,
        RecaptchaV3Module,
        MatProgressSpinnerModule,
        JwtModule.forRoot({
            config: {
                tokenGetter,
                throwNoTokenError: true,
                authScheme: "",
                whitelistedDomains: environment.whitelist,
            },
        }),
        AgmCoreModule.forRoot({
            apiKey: process.env.GOOGLE_API_KEY,
            // On 3.65: 3.64 is deleted at the mid-February 2027 quarterly
            // rollover, and every version that outlives it lacks DrawingManager
            // (removed in v3.65.3b) and HeatmapLayer (removed in v3.65).
            //
            // Drawing no longer depends on either - all four shapes are captured
            // in-house in map.component.ts. The heatmap is still outstanding and
            // degrades to a toast via its runtime guard; see
            // docs/HEATMAP-IN-HOUSE-REPLACEMENT.md.
            apiVersion: "3.65",
            // "drawing" is commented out: DrawingManager is gone from 3.65+, so
            // requesting it is a no-op, and nothing reads google.maps.drawing any
            // more. "geometry" used to arrive transitively via "drawing", so it is
            // requested explicitly - the geometry.spherical call sites, including
            // the in-house capture tools, depend on it.
            // libraries: ["drawing", "places", "visualization", "geometry"],
            libraries: ["places", "visualization", "geometry"],
        }),
        NgxEchartsModule.forRoot({
            echarts: echartImport,
        }),
        DatasetModule,
        MaiaModule,
        InfiniteScrollModule,
    ],
    providers: [
        ReCaptchaV3Service,
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
        {
            provide: APP_INITIALIZER,
            useFactory: (analytics: AnalyticsInitializerService) => () => analytics.load(),
            deps: [AnalyticsInitializerService],
            multi: true
        }
    ],
    bootstrap: [AppComponent],
})

export class AppModule {
}
